import { Button } from '@aecom-ai-engineering/react-components';
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import {
  useCurrentLanguage,
  useInviteUserMutation,
  useMyUser,
  useProjectId,
} from '@consigli/hooks';
import {
  OrganizationRole,
  ProjectRole,
  type SendInviteRequest,
  type ServicePermissionName,
} from '@consigli/types';
import { Info, SendHorizontal, UserPlus, X } from 'lucide-react';
import { type FC, type RefObject, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { AGENT_MARKS } from '@/components/agent-marks';
import { DialogSelect } from '@/components/dialog-select';
import { useProjectServices } from '@/features/project-services/use-project-services';

import { InviteAgentOption } from './invite-agent-option';
import { InviteCheckbox } from './invite-checkbox';
import { InviteRecipientsField } from './invite-recipients-field';
import './invite-users-panel.css';

/** The weight the redesign draws its lucide marks at. */
const ICON_STROKE_WIDTH = 1.75;

/** The frame's 800px panel, measured from a 1416px viewport. */
const PANEL_WIDTH = 'w-full max-w-[800px]';

interface InviteUsersPanelProps {
  onClose: () => void;
  /** Drives the dialog. The panel stays mounted so it can animate out. */
  open: boolean;
  /** Named in the subtitle. Absent while the project is still loading. */
  projectName?: string;
}

interface InviteUsersFormProps {
  /** The dialog's content element, which the Role menu portals into. */
  contentRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  projectName?: string;
}

/**
 * Everything inside the panel: the three bands the frame draws, and the state
 * behind them.
 *
 * Separate from the shell below because the shell keeps the dialog mounted for
 * its exit animation, and this must not be: it holds the recipients, the role
 * and the ticks, and a reopened panel has to be empty. `lazyMount` and
 * `unmountOnExit` unmount the content subtree once the slide-out ends, so this
 * component — and its state with it — is built fresh on every open. That is
 * the reset, and it is why there is no effect doing it by hand.
 *
 * ## What the invite carries
 *
 * Access to this project, and membership of the inviter's organisation.
 *
 * The membership is how the API spells "this person may create projects":
 * the legacy form's `canCreateProjects` checkbox does nothing but add
 * `organization` to the request, and `pages/project-list/new-project-button.tsx`
 * shows the Create Project action to anyone with an `organizationId`. FA-116
 * removes the choice — no checkbox is drawn and every invite carries it.
 *
 * The inviter's organisation, which is what `invite-user-form.tsx` sends:
 * `const id = user.organizationId!`, unconditionally, never a project's.
 *
 * This did briefly read `project.organizationId` instead. The two differ only
 * when an org admin invites through a project another organisation owns,
 * which the legacy project picker does allow — and in that case naming the
 * project's organisation asks the server to add someone to an organisation
 * the inviter does not belong to. No surface in this product has ever sent
 * that, so whether it is refused or quietly honoured is unknown. Matching the
 * old flow keeps the question closed.
 *
 * Always USER, whatever the project role above. Project admin is not
 * organisation admin, and nothing here should be able to grant the latter.
 */
const InviteUsersForm: FC<InviteUsersFormProps> = ({ contentRef, onClose, projectName }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const language = useCurrentLanguage();
  const { user } = useMyUser();
  const { services } = useProjectServices();
  const [inviteUsers] = useInviteUserMutation();

  const [recipients, setRecipients] = useState<string[]>([]);
  /*
   * Admin, per 11930:195845, which draws the panel's resting state with the
   * role already picked. Not the narrower of the two, so it is worth being
   * plain about: the panel opens offering the wider grant, and an inviter who
   * wants the narrower one switches to User and ticks the agents by hand.
   * Product's call, drawn rather than inferred.
   */
  const [role, setRole] = useState<ProjectRole>(ProjectRole.ADMIN);
  const [permissions, setPermissions] = useState<ServicePermissionName[]>([]);
  const [isSending, setIsSending] = useState(false);

  /*
   * Only the services an invite can actually grant. `useProjectServices` also
   * returns the three whose access is a user-level flag rather than a project
   * permission — Solution Viewer, Data Center and Wastewater treatment carry no
   * servicePermissionName, so there is nothing to put in the request for them
   * and a ticked row would silently do nothing.
   *
   * The hook has already narrowed the list to this project's effective services
   * and to what the signed-in user can see, which is the same pair of filters
   * the legacy `ProjectUser` step applies. An inviter cannot grant what they
   * cannot see.
   */
  const grantable = useMemo(
    () =>
      services.flatMap((service) =>
        service.servicePermissionName === undefined
          ? []
          : [{ ...service, servicePermissionName: service.servicePermissionName }],
      ),
    [services],
  );

  // A project admin reaches every agent by virtue of the role, so the list is
  // not a choice for them — the same reason the legacy step hides its service
  // checkboxes once Project Admin is ticked.
  const isAdmin = role === ProjectRole.ADMIN;
  const allSelected = grantable.length > 0 && permissions.length === grantable.length;

  /*
   * Null for a superuser, who belongs to no organisation. The legacy form
   * asserts this field (`user.organizationId!`) and keeps the case away from
   * it by other means — it disables its own submit for a superuser and warns
   * them not to invite at all. Here the same case simply holds the send, so
   * the request cannot go out naming an organisation that is not there.
   */
  const organizationId = user.organizationId ?? undefined;

  const roleItems = useMemo(
    () => [
      { label: t('project-overview.user-type-user'), value: ProjectRole.USER },
      { label: t('project-overview.user-type-admin'), value: ProjectRole.ADMIN },
    ],
    [t],
  );

  const togglePermission = (permission: ServicePermissionName, checked: boolean) =>
    setPermissions((previous) =>
      checked ? [...previous, permission] : previous.filter((existing) => existing !== permission),
    );

  const handleSubmit = async () => {
    if (recipients.length === 0 || isSending || organizationId === undefined) return;

    const request: SendInviteRequest = {
      language,
      recipients,
      projects: [
        {
          id: projectId,
          role,
          // An admin's permissions are implied by the role. Sending the boxes
          // that happened to be ticked before the role was switched would
          // record a narrower grant than the role gives.
          permissions: isAdmin ? [] : permissions,
        },
      ],
      // The inviter's organisation, at USER — the same id and the same role
      // the legacy form sends. See the note on this component.
      organization: { id: organizationId, role: OrganizationRole.USER },
    };

    try {
      setIsSending(true);
      const created = await inviteUsers(request).unwrap();
      // The response length rather than the request's: the API may reject part
      // of a batch, and the toast should count what was actually sent.
      const count = created?.length ?? recipients.length;
      toast.success(t('invite-user.invite-success-count', { count }));
      onClose();
    } catch (error) {
      console.error('Error sending invite:', error);
      // The same two arms the legacy form distinguishes: a batch the server
      // refused outright, against anything else.
      const status = (error as { status?: number } | undefined)?.status;
      toast.error(
        t(
          status === 400 || status === 403
            ? 'invite-user.invite-batch-rejected'
            : 'invite-user.invite-failure',
        ),
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="flex shrink-0 items-center gap-4 border-b border-solid border-[var(--ds-color-border-primary)] px-6 py-4">
        {/* The frame's 48px tile on brand-light, with `Icon / user-plus`
            inside it at 16 — 11949:196324, which names the mark the earlier
            frame left as a plain `user`. The same glyph the page's Invite
            Users button carries, which is the point: the tile is that
            button's destination. Decorative, since the title beside it names
            the panel. */}
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-[var(--ds-color-bg-brand-light)]">
          <UserPlus
            aria-hidden="true"
            className="size-4 text-[var(--ds-color-bg-icon-primary)]"
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* 18/22 at weight 500. The design system's heading-5 token is
              16px, so the size is set explicitly; the drift is the same
              one project-overview-header.tsx records for heading-4. */}
          <Dialog.Title className="truncate text-lg font-medium leading-[22px] text-[var(--ds-color-text-primary)]">
            {t('project-overview.invite-title')}
          </Dialog.Title>
          {/* Only once the name has arrived. The frame draws
              "Invite users to {project_name}", and the panel can be
              opened while the project fetch is still in flight — a
              subtitle with a gap where the name goes is worse than none. */}
          {projectName !== undefined && (
            <Dialog.Description className="truncate text-[13px] leading-5 text-[var(--ds-color-raw-stone-600)]">
              {t('project-overview.invite-subtitle', { name: projectName })}
            </Dialog.Description>
          )}
        </div>

        <Dialog.CloseTrigger
          aria-label={t('project-overview.invite-close')}
          className="invite-panel-close flex size-6 shrink-0 cursor-pointer items-center justify-center text-[var(--ds-color-bg-icon-primary)]"
        >
          <X aria-hidden="true" className="size-6" strokeWidth={ICON_STROKE_WIDTH} />
        </Dialog.CloseTrigger>
      </div>

      {/* The one scrolling band. The header above and the footer below
          are `shrink-0`, so a long agent list moves under them rather
          than pushing Send Invitations off the bottom. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto border-b border-solid border-[var(--ds-color-border-primary)] px-6 py-4">
        <InviteRecipientsField disabled={isSending} onChange={setRecipients} value={recipients} />

        <div className="flex flex-col gap-2">
          {/* Select.css clips the control's own label to a 2px box, and
              the frame draws a visible one. So the visible copy is
              written here and hidden from assistive tech, and the same
              string goes to the control as its accessible name — one
              name announced, one label seen. */}
          <span
            aria-hidden="true"
            className="text-sm font-medium leading-5 text-[var(--ds-color-raw-stone-950)]"
          >
            {t('project-overview.invite-role')}
          </span>
          <DialogSelect
            container={contentRef}
            disabled={isSending}
            label={t('project-overview.invite-role')}
            onValueChange={(next) =>
              setRole(next === ProjectRole.ADMIN ? ProjectRole.ADMIN : ProjectRole.USER)
            }
            options={roleItems}
            value={role}
          />
        </div>

        {/* The heading stays for both roles — 11930:195845 keeps it over
            the admin note — and only what sits under it changes. The
            frames put 8 under the heading in the admin state and 16 in
            the user state. */}
        <div className={`flex flex-col ${isAdmin ? 'gap-2' : 'gap-4'}`}>
          <div className="flex items-center gap-4">
            <p className="min-w-0 flex-1 text-sm font-medium leading-5 text-[var(--ds-color-text-primary)]">
              {t('project-overview.invite-agent-access')}
            </p>
            {/* No Select All in the admin state: there is nothing to
                select, and the frame does not draw it. */}
            {!isAdmin && grantable.length > 0 && (
              <InviteCheckbox
                checked={allSelected}
                indeterminate={permissions.length > 0 && !allSelected}
                label={t('project-overview.invite-select-all')}
                onCheckedChange={(checked) =>
                  setPermissions(
                    checked ? grantable.map((service) => service.servicePermissionName) : [],
                  )
                }
              />
            )}
          </div>

          {isAdmin ? (
            /*
             * Why the list is gone, rather than leaving its absence to be
             * inferred. role=status so the swap is announced: a sighted
             * user sees the grid replaced by this, and without a live
             * region a screen-reader user hears the agent rows disappear
             * and nothing in their place.
             *
             * It does not announce on open, even though admin is the
             * default — a live region present when its container mounts
             * is read as content, not as an update.
             */
            <p
              className="flex items-center gap-2 rounded-lg bg-[var(--ds-color-bg-secondary)] px-3 py-2 text-sm leading-5 text-[var(--ds-color-text-primary)]"
              role="status"
            >
              <Info
                aria-hidden="true"
                className="size-4 shrink-0"
                strokeWidth={ICON_STROKE_WIDTH}
              />
              <span className="min-w-0 flex-1">
                {t('project-overview.invite-admin-full-access')}
              </span>
            </p>
          ) : grantable.length === 0 ? (
            <p className="text-[13px] leading-[18px] text-[var(--ds-color-raw-stone-600)]">
              {t('project-overview.invite-no-agents')}
            </p>
          ) : (
            // Two columns as the frame draws them, collapsing to one
            // below 640 rather than squeezing a logo, a title and a box
            // into half of a phone.
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {grantable.map((service) => (
                <InviteAgentOption
                  checked={permissions.includes(service.servicePermissionName)}
                  key={service.serviceName}
                  mark={AGENT_MARKS[service.serviceName]}
                  onCheckedChange={(checked) =>
                    togglePermission(service.servicePermissionName, checked)
                  }
                  title={service.title}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 px-6 py-4">
        <Button disabled={isSending} onClick={onClose} size="md" variant="outline">
          {t('project-overview.settings-cancel')}
        </Button>
        {/* `organizationId === undefined` is the superuser case, where there
            is no organisation to name. Held rather than sent without one,
            which the server would refuse and the panel would report as a
            plain failure. */}
        <Button
          disabled={recipients.length === 0 || isSending || organizationId === undefined}
          onClick={() => void handleSubmit()}
          size="md"
          variant="solid"
        >
          {/* 11949:196339 draws `Icon / send-horizontal` before the label. No
              size class and no gap of its own: Button.css sizes `> svg` to 16
              at md and already sets the frame's 8 between them, which is the
              call project-overview-header.tsx made for the same button at the
              same size.

              Decorative — the words beside it are the action, and while the
              send is in flight it is those words that change, not the mark. */}
          <SendHorizontal aria-hidden="true" strokeWidth={ICON_STROKE_WIDTH} />
          {t(isSending ? 'project-overview.invite-sending' : 'project-overview.invite-send')}
        </Button>
      </div>
    </>
  );
};

/**
 * The project's invite panel: recipients, a role, and which agents the invited
 * users may open. Figma 11930:195537.
 *
 * A full-height sheet against the right edge rather than a centred dialog,
 * which is how the frame places it — 800 wide against a 1416 viewport, flush
 * top, right and bottom. Ark's Dialog rather than its Drawer: nothing here
 * resizes or minimises, so the drawer's machinery would be unused, and the
 * positioner is the whole of the difference. Composed rather than taking the
 * design system's Dialog for the reason `add-context-modal.tsx` records — that
 * component owns its own header and footer structure, is capped at 640, and
 * this frame's three bands at 800 are not it.
 *
 * ## Why it stays mounted
 *
 * `open` rather than a conditional mount by the caller. The slide-out needs
 * the panel on screen while it plays, and a parent that unmounts on close
 * takes it away mid-animation. Ark's presence handles the rest: it holds the
 * content until `animationend`, then unmounts it — which is also what empties
 * the form for the next open. See InviteUsersForm.
 *
 * ## Why it is behind the flag without asking
 *
 * `ProjectOverviewHeader` renders this, and `ProjectOverviewRoute` renders that
 * header only once `FeatureFlag.AppRedesign` is on — flag-off, these URLs
 * redirect to the legacy service list. The header carries the explicit check
 * so the gate is stated at the branch rather than inferred from the route.
 */
export const InviteUsersPanel: FC<InviteUsersPanelProps> = ({ onClose, open, projectName }) => {
  /*
   * Where the Role menu is portaled. The dialog's content rather than the
   * scrolling body inside it — see components/dialog-select.tsx for both
   * halves of why it cannot be `<body>` and cannot be the scroller.
   */
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog.Root
      closeOnEscape
      // Closing on an outside click, which is what a dialog does. It does cost
      // a typed list of addresses to a stray click on the page behind — the
      // panel keeps nothing between opens — so this is the trade the product
      // asked for rather than a default that fell out.
      closeOnInteractOutside
      lazyMount
      onOpenChange={({ open: next }) => {
        if (!next) onClose();
      }}
      open={open}
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className="invite-panel-backdrop fixed inset-0 z-80 bg-[color-mix(in_srgb,var(--ds-color-raw-black)_20%,transparent)]" />
        <Dialog.Positioner className="fixed inset-y-0 right-0 z-80 flex w-full justify-end">
          <Dialog.Content
            className={`invite-panel ds-body-font flex h-full flex-col bg-[var(--ds-color-bg-primary)] ${PANEL_WIDTH}`}
            ref={contentRef}
          >
            <InviteUsersForm contentRef={contentRef} onClose={onClose} projectName={projectName} />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
